import type * as React from "react";

import { LIFT_STEP, surface } from "@/shared/components/ui/elevation";
import { cn } from "@/shared/lib/cn";

/**
 * The card — the ONE surface a console view stacks its content on.
 *
 * It wears the elevation scale, never an ad hoc shadow: `surface.panel` (elevation.ts) on a
 * hairline edge, so it holds its own shape against the canvas. In
 * the dark theme the card SURFACE is one step lighter than the canvas
 * (`--card` in globals.css) because a shadow cannot show against a
 * background it matches. `interactive` is for cards that are links or
 * buttons: they lift to `raised` under the pointer and settle back, which
 * is how a clickable object says it is one without a label having to.
 */
/**
 * The card's surface and its lift, as classes — for the element that must
 * BE something else (a link tile, a button, a form) and still stand on the
 * page as a card. One recipe, so a clickable tile and a `<Card>` can never
 * drift apart by a shade of border or a step of shadow.
 */
export const cardSurface = `rounded-xl text-card-foreground ${surface.panel}`;
export const cardLift = LIFT_STEP;

function Card({
  className,
  interactive = false,
  ...props
}: React.ComponentProps<"div"> & { interactive?: boolean }) {
  return (
    <div
      data-slot="card"
      data-interactive={interactive || undefined}
      className={cn("flex flex-col gap-6 py-6", cardSurface, interactive && cardLift, className)}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("text-[15px] leading-none font-semibold tracking-tight", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-[13px] leading-snug text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("px-6", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
